import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { consultPatientActions } from 'src/app/core/component/consult-patient/store/consult-patient.action';
import { RestService } from 'src/app/core/service/rest.service';
import { Utils } from 'src/app/core/utils/khuyana.utils';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { commonTurnActions } from 'src/app/store/common/turn/turn.action';
import { generalConsultActions } from 'src/app/store/general/general.action';
import { historyActions } from 'src/app/store/history/history.action';
import { environment } from 'src/environments/environment';
import { PatientDialogComponent } from '../../common/patient/components/patient-dialog.component';
import { PatientService } from '../../common/patient/patient.service';
import { PersonService } from '../../common/person/person.service';
import { commonQuestionActions } from 'src/app/store/common/question/question.action';

@Injectable({
  providedIn: 'root',
})
export class GeneralConsultService extends BaseService {
  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected router: Router,
    protected dialogService: DialogService,
    protected patientService: PatientService,
    protected personService: PersonService
  ) {
    super();
  }

  loadPatient = this.patientService.getPatientById;

  loadPerson = this.personService.getPersonById;

  getSavingState = (): Observable<boolean> =>
    this.store.select('general', 'consult', 'saving');

  savedConsult = (): Observable<boolean> =>
    this.store.select('general', 'consult', 'save');

  savedHistory = (): Observable<boolean> =>
    this.store.select('history', 'save');

  readTurnStatusStore = (): Observable<AdminCatalogModel[]> =>
    this.store.select('admin', 'catalog', 'subCatalogs', 'turn_status');

  getTurn = (id: number): Observable<CommonTurnViewModel> =>
    this.rest
      .get({
        url: `${environment.endpoints.common}/turn/{id}`,
        urlParams: { id },
      })
      .pipe(
        tap((turn) =>
          this.store.dispatch(
            historyActions.setPersonIdAction({ personId: turn.person! })
          )
        )
      );

  getOldTurns = (
    patient: number,
    turn: number
  ): Observable<CommonTurnViewModel[]> =>
    this.rest.get({
      url: `${environment.endpoints.common}/turn/not/{turn}/patient/{patient}`,
      urlParams: { patient, turn },
    });

  getPatient = (id: number): Observable<CommonPatientViewModel> =>
    this.rest
      .get({
        url: `${environment.endpoints.common}/patient/{id}/view`,
        urlParams: { id },
      })
      .pipe(
        tap((patient) =>
          this.store.dispatch(
            generalConsultActions.setPatientAction({ patient: patient.id })
          )
        ),
        map((patient) => {
          if (patient.dateBorn) {
            patient.dateBorn = new Date(patient.dateBorn);
          }
          patient.ageLabel = Utils.transformAge(patient.age);
          return patient;
        })
      );

  save = () => {
    this.store.dispatch(historyActions.setSavingAction({ saving: true }));
    this.store.dispatch(
      generalConsultActions.setSavingAction({ saving: true })
    );
  };

  finalSave = (
    isFinal: boolean,
    turnId: number,
    statusId: string
  ): Observable<CommonTurnViewModel> => {
    this.store.dispatch(historyActions.setSaveAction({ save: false }));
    this.store.dispatch(generalConsultActions.setSaveAction({ save: false }));
    if (isFinal) {
      return this.moveTurnStatus(turnId, statusId).pipe(
        tap((turn) =>
          this.store.dispatch(commonTurnActions.patchResultAction(turn))
        ),
        tap(() => this.router.navigate(['/turno']))
      );
    }
    return of({});
  };

  moveTurnStatus = (id: number, newStatus: string) =>
    this.rest
      .patch<CommonTurnViewModel>({
        url: `${environment.endpoints.common}/turn/{id}/status/{newStatus}`,
        urlParams: { id, newStatus },
        params: { status: true },
      })
      .pipe(
        tap((turn) =>
          this.store.dispatch(commonTurnActions.patchResultAction(turn))
        ),
        tap(() =>
          this.store.dispatch(
            commonTurnActions.generatedRecipeAction({
              recipeFile: undefined,
              showRecipePdf: false,
            })
          )
        )
      );

  generateCertificate = (
    historyNumber: string,
    turnId: number
  ): Observable<SafeResourceUrl> =>
    this.rest
      .get({
        url: `${environment.endpoints.report}/medical-history/{historyNumber}/{turnId}`,
        urlParams: { historyNumber, turnId },
        header: {
          responseType: 'BLOB' as 'json',
        },
      })
      .pipe(
        map(this.rest.mapResponseAsPdf),
        tap((file) =>
          this.store.dispatch(
            consultPatientActions.setHistoryPdfAction({ file })
          )
        )
      );

  openPatientDialog = (): DynamicDialogRef =>
    this.dialogService.open(PatientDialogComponent, {
      data: {
        save: this.patientService.save,
      },
      header: 'Crear / Editar paciente',
      closeOnEscape: true,
      width: '75%',
    });

  getParametrizationBySpecialtyAndPatient = (
    specialty: string,
    patient: number
  ) =>
    this.rest
      .get({
        url: `${environment.endpoints.commonParametrization}/question/{specialty}/{patient}`,
        urlParams: { specialty, patient },
      })
      .pipe(
        tap((questions) => {
          const topQuestions = questions.filter(
            (question: any) => question.showOnTop
          );
          const bottomQuestions = questions.filter(
            (question: any) => !question.showOnTop
          );
          this.store.dispatch(
            commonQuestionActions.setConsultTopQuestionsAction({
              topQuestions,
            })
          );
          this.store.dispatch(
            commonQuestionActions.setConsultBottomQuestionsAction({
              bottomQuestions,
            })
          );
        })
      );
}
